export const DEFAULT_TRAINING_HOURS = '8:00 AM - 4:30 PM'

const DAY_NAME_TO_NUMBER: Record<string, number> = {
  Sunday: 0,
  Monday: 1,
  Tuesday: 2,
  Wednesday: 3,
  Thursday: 4,
  Friday: 5,
  Saturday: 6
}

export const YARD_DAY_OPTIONS = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday'
] as const

export type TrainingProgramMode = 'classroom-only' | 'yard-only' | 'combined'

export type ClassroomScheduleDisplay = {
  label: string
  value: string
}

export type TrainingStartDateRules = {
  allowedDays: number[]
  availableStartDates: string[]
  minimumStartDate?: string
  unavailableStartDate?: string | null
}

export function formatHour(time: string | null | undefined): string | null {
  if (!time) return null

  const [hourStr, minuteStr = '00'] = time.split(':')
  const hour = Number.parseInt(hourStr, 10)

  if (Number.isNaN(hour)) return null

  const minute = minuteStr ?? '00'
  const period = hour >= 12 ? 'PM' : 'AM'
  const displayHour = hour % 12 === 0 ? 12 : hour % 12

  return `${displayHour}:${minute} ${period}`
}

export function formatHourRange(
  openingHour: string | null | undefined,
  closingHour: string | null | undefined
): string {
  const formattedOpeningHour = formatHour(openingHour)
  const formattedClosingHour = formatHour(closingHour)

  return formattedOpeningHour && formattedClosingHour
    ? `${formattedOpeningHour} - ${formattedClosingHour}`
    : DEFAULT_TRAINING_HOURS
}

export function getTrainingProgramMode({
  storedProgramYardOnly,
  storedProgramClassOnly
}: {
  storedProgramYardOnly: boolean
  storedProgramClassOnly: boolean
}): TrainingProgramMode {
  const isYardOnly = storedProgramYardOnly
  const isClassroomOnly = storedProgramClassOnly

  return isYardOnly ? 'yard-only' : isClassroomOnly ? 'classroom-only' : 'combined'
}

export function getAllowedTrainingDays(programMode: TrainingProgramMode, yardDays: string[]) {
  return programMode === 'yard-only'
    ? yardDays.map(day => DAY_NAME_TO_NUMBER[day]).filter((day): day is number => day !== undefined)
    : [1]
}

export function getClassroomScheduleDisplay(
  trainingMode: string | null | undefined,
  classroomHours: string
): ClassroomScheduleDisplay {
  return trainingMode === 'remote'
    ? {
        label: 'Virtual classroom schedule',
        value: 'Your class time will be confirmed before training.'
      }
    : {
        label: 'Classroom training hours',
        value: classroomHours
      }
}

export function isTrainingStartDateAllowed(
  trainingStartDate: string,
  rules: TrainingStartDateRules
): boolean {
  if (!trainingStartDate) return false
  if (rules.minimumStartDate && trainingStartDate < rules.minimumStartDate) return false
  if (trainingStartDate === rules.unavailableStartDate) return false
  if (rules.availableStartDates.length > 0) {
    return rules.availableStartDates.includes(trainingStartDate)
  }
  const selectedDate = new Date(`${trainingStartDate}T00:00:00`)
  return !Number.isNaN(selectedDate.getTime()) && rules.allowedDays.includes(selectedDate.getDay())
}
